import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Hook specifically for admin blog posts (for AdminDashboard component)
export const useAdminPosts = () => {
  const { clearAuth } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get session ID from localStorage
  const getSessionId = () => localStorage.getItem('adminSessionId');

  // Get auth headers for admin requests
  const getAuthHeaders = () => {
    const sessionId = getSessionId();
    if (!sessionId) {
      throw new Error('No session found. Please login again.');
    }
    return { 'X-Session-Id': sessionId };
  };

  // Clear authentication when session is invalid
  const clearAuthOnError = () => {
    clearAuth();
    window.location.href = '/blog/admin/login';
  };

  // Load all posts for admin (including drafts) - manual call only
  const loadAdminPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/posts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
      });

      if (response.status === 401) {
        // Session expired or invalid
        clearAuthOnError();
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const posts = await response.json();
      setPosts(posts || []);
      return { success: true };
    } catch (err) {
      console.error('Failed to load admin posts:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new post
  const createPost = useCallback(async (postData) => {
    try {
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(postData),
      });

      if (response.status === 401) {
        clearAuthOnError();
        throw new Error('Session expired. Please login again.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create post');
      }

      // Reload posts after creation
      await loadAdminPosts();
      
      return {
        success: true,
        post: { ...postData, id: result.id, slug: result.slug },
        errors: []
      };
    } catch (err) {
      console.error('Failed to create post:', err);
      setError(err.message);
      return {
        success: false,
        post: null,
        errors: [err.message]
      };
    }
  }, []); // Remove loadAdminPosts dependency to prevent circular reference

  // Update an existing post
  const updatePost = useCallback(async (postId, postData) => {
    try {
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(postData),
      });

      if (response.status === 401) {
        clearAuthOnError();
        throw new Error('Session expired. Please login again.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update post');
      }

      // Reload posts after update
      await loadAdminPosts();

      return {
        success: true,
        post: { ...postData, id: postId, slug: result.slug },
        errors: []
      };
    } catch (err) {
      console.error('Failed to update post:', err);
      setError(err.message);
      return {
        success: false,
        post: null,
        errors: [err.message]
      };
    }
  }, []); // Remove loadAdminPosts dependency to prevent circular reference

  // Delete a post
  const deletePost = useCallback(async (postId) => {
    try {
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
      });

      if (response.status === 401) {
        clearAuthOnError();
        throw new Error('Session expired. Please login again.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete post');
      }

      // Remove post from local state
      setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));

      return {
        success: true,
        errors: []
      };
    } catch (err) {
      console.error('Failed to delete post:', err);
      setError(err.message);
      return {
        success: false,
        errors: [err.message]
      };
    }
  }, []);

  // Get a single post by ID - memoized to prevent infinite loops
  const getPostById = useCallback((id) => {
    return posts.find(post => post.id == id);
  }, [posts]);

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return {
    posts,
    loading,
    error,
    loadAdminPosts,
    createPost,
    updatePost,
    deletePost,
    getPostById,
    clearError
  };
};

export default useAdminPosts;